import { createRouter, createWebHistory } from 'vue-router'
import BlankLayoutRoutes from './BlankLayoutRoutes'
import AuthRoutes from './AuthRoutes'
import MainRoutes from './MainRoutes'
import UserRoutes from './UserRoutes'
import FormRoutes from './FormRoutes'
import FrameRoutes from './FrameRoutes'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/companyStore'
import { useMenuStore } from '@/stores/menuStore'
import { ElNotification } from 'element-plus'
import axios from '@/utils/apiClient'

const routes = [
    { path: '/' }, // only default, will be handled in guard
    MainRoutes,

    AuthRoutes,
    BlankLayoutRoutes,
    UserRoutes,
    FormRoutes,
    ...FrameRoutes,
    { path: '/:pathMatch(.*)*', component: () => import('@/views/authentication/Error.vue') },
]

// System utility routes that are always accessible to authenticated users
const ALWAYS_ALLOWED_ROUTES = new Set([
    '/dashboard',
    '/error',
    '/auth/expired',
    '/user/leave',
    '/auth/change-password',
    '/user/menu',
    '/user/profile',
    '/user/change-password',
    '/user/notification',
    '/user/qr-attendance',
    '/staffing/employeeprofile/:id',
    '/user/performance',
    '/user/overtime',
    '/user/probationary-list',
])

function isAlwaysAllowed(path: string): boolean {
    const p = path.toLowerCase()

    // 1. Always allow file downloads, exports, uploads, static content, and file extensions
    if (
        p.includes('/content/') ||
        p.includes('/tempfile') ||
        p.includes('/uploads/') ||
        p.includes('/api/') ||
        p.includes('/export') ||
        p.includes('/download') ||
        p.includes('/documents/') ||
        p.includes('/signature/') ||
        /\.(xlsx|xls|pdf|doc|docx|csv|zip|rar|png|jpg|jpeg|gif|svg|txt)$/i.test(p)
    ) {
        return true
    }

    // 2. Always allowed system routes
    if (ALWAYS_ALLOWED_ROUTES.has(p)) return true

    const parts = p.split('/')
    if (parts.length > 2) {
        const parentPath = parts.slice(0, -1).join('/')
        if (ALWAYS_ALLOWED_ROUTES.has(parentPath) || ALWAYS_ALLOWED_ROUTES.has(parentPath + '/:id')) {
            return true
        }
    }

    return false
}

// Determine base path dynamically
const basePath = window.location.pathname.startsWith('/hr') ? '/hr/' : ''

export const router = createRouter({
    history: createWebHistory(basePath),
    routes,
})

router.beforeEach(async (to, from, next) => {
    const auth = useAuthStore()
    const companyStore = useCompanyStore()
    const publicPages = ['/auth/login', '/auth/change-password']

    // Check if the route is under BlankLayout (no auth required)
    // We check the meta.requiresAuth on the parent route or route itself
    const isBlankLayoutRoute = to.matched.some(record => record.meta.requiresAuth === false)

    const authRequired = !publicPages.includes(to.path) && !isBlankLayoutRoute

    // Restore session if missing
    if (!auth.user) {
        const stored = localStorage.getItem('user')
        if (stored) {
            try {
                const parsed = JSON.parse(stored)
                auth.user = parsed
                // Set axios token synchronously so subsequent API calls include Bearer token
                axios.defaults.headers.common['Authorization'] = `Bearer ${parsed.token}`
                // Restore company
                if (auth.user.company) {
                    companyStore.company = auth.user.company
                    companyStore.companyID = auth.user.company.id
                } else if (companyStore.companies.length > 0) {
                    companyStore.company = companyStore.companies[0]
                    companyStore.companyID = companyStore.companies[0].id
                }
                // Redirect logged-in user from login page
                if (to.path === '/auth/login') {
                    return next(auth.user.homePage || '/')
                }
            } catch (err) {
                console.error('Failed to restore session:', err)
                auth.clearSession()
                return next('/auth/login')
            }
        }
    }

    // Require auth
    if (authRequired && !auth.user) {
        auth.returnUrl = to.fullPath
        return next('/auth/login')
    }

    // Redirect `/` to user homePage
    if (to.path === '/' && auth.user?.homePage) {
        return next(auth.user.homePage)
    }

    // 🔒 Menu-based route access guard
    if (auth.user && authRequired) {
        if (!isAlwaysAllowed(to.path)) {
            const menuStore = useMenuStore()
            // Ensure menu is loaded (fetches from API once if not yet loaded)
            await menuStore.fetchAndSetMenu()

            if (!menuStore.isRouteAllowed(to.path)) {
                ElNotification({
                    type: 'warning',
                    title: 'Access Denied',
                    message: 'You do not have permission to access this page.',
                })
                // Redirect to user's home page or dashboard
                const homePage = auth.user.homePage || '/dashboard'
                return next(homePage)
            }
        }
    }

    next()
})
